import { Inject, Injectable } from '@angular/core';
import { merge, Observable, of, Subject, timer } from 'rxjs';
import { catchError, flatMap, map, shareReplay } from 'rxjs/operators';
import { isInternalEmail, isInternalPass } from 'src/app/helpers/misc';
import { environment } from '../../../environments/environment';
import { HttpHelper } from '../../helpers/HttpHelper';
import { MyRegistration } from '../../models/MyRegistration';
import {Pass} from '../../models/Pass';
import {DecodedUser, User} from '../../models/User';
import { WaitlistItem } from '../../models/WaitlistItem';
import { HTTP_HELPER } from '../../providers/http.helper.provider';
import { UserBaseService } from '../base/user.base.service';

@Injectable({
    providedIn: 'root'
})
export class UserRestService extends UserBaseService {

    private static readonly usersEndpoint: string = 'users';
    private static readonly timerPeriodInMillis: number = 15 * 60 * 1000; // 15 minutes

    private getLoggedInUserApiCall$: Observable<User>;

    private manualUpdateObserver: Subject<any> = new Subject();

    constructor(@Inject(HTTP_HELPER) private http: HttpHelper) {
        super();
        this.getLoggedInUserApiCall$ = merge(timer(0, UserRestService.timerPeriodInMillis), this.manualUpdateObserver)
        .pipe(
            flatMap((i) => this.http.get<User>(`${environment.apiEndpoint}${UserRestService.usersEndpoint}/authenticated`, true)),
            map((user) => this.mapExtensionAttributes(user)),
            catchError((error) => of(UserBaseService.AnonymousUser)),
            shareReplay(1));
    }

    public refreshUser(): void {
        this.manualUpdateObserver.next();
    }

    public getLoggedInUser(): Observable<User> {
      return this.getLoggedInUserApiCall$;
    }

    public isLoggedIn(): Observable<boolean> {
        return this.getLoggedInUser().pipe(map((user) => user != null && !user.isAnonymous));
    }

    public getRegistrationsForUser(): Observable<MyRegistration[]> {
        return this.http.get<MyRegistration[]>(
            `${environment.apiEndpoint}${UserRestService.usersEndpoint}/authenticated/registrations`, true
        );
    }

    public getWaitlistItemsForUser(): Observable<WaitlistItem[]> {
        return this.http.get<WaitlistItem[]>(`${environment.apiEndpoint}${UserRestService.usersEndpoint}/authenticated/waitlist-items`);
    }

    public cancelRegistration(registrationId: string): Observable<boolean> {
      return this.http.post<boolean>(
            `${environment.apiEndpoint}${UserRestService.usersEndpoint}/authenticated/registrations/${registrationId}/cancel`, {}, true
        );
    }

    private getDecodedUser(user: User): DecodedUser {
      const userFromCurrentToken: DecodedUser = this.http.decodedUserToken() as any;
      if (userFromCurrentToken) {
        if (userFromCurrentToken.emails.some((email) => email.toLocaleLowerCase() === user.email) &&
          userFromCurrentToken.given_name === user.firstName &&
          userFromCurrentToken.family_name === user.lastName) {
          return userFromCurrentToken;
        }
        console.error('authentication token does not match with logged in user');
      }
      return null;
    }

    private mapExtensionAttributes(user: User): User {
      user.extension_Company = this.getDecodedUser(user)?.extension_Company ?? '';
      return user;
    }

   public getFilteredPassesByUser(passes: Pass[], user: User): Pass[] {
     if (!environment.isAuthenticationEnabled) {
      return passes;
     }

     let filteredPasses = passes;

     if (!this.getDecodedUser(user)?.emails.some((email) => {
       return isInternalEmail(email);
     })) {
       filteredPasses = passes.filter((pass) => {
         return !isInternalPass(pass);
       });
     }

     return filteredPasses;
  }
}
